/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import useBaseService from '../../../hooks/services/useBaseService'
import useLoader from '../../../hooks/useLoader'
import useNotification from '../../../hooks/useNotification'
import { entities } from '../../../constants/entities'
import MainLayout from '../../../layouts/MainLayout'
import PageHeader from '../../../components/PageHeader'
import { faFilePdf } from '@fortawesome/free-solid-svg-icons'
import useForm from '../../../hooks/useForm'
import { cleanPayload, setDefaultFieldValues } from '../../../utils/objectUtils'
import SimpleBody from '../../../components/forms/SimpleBody'
import SectionHeader from '../../../components/forms/SectionHeader'
import Form from '../../../components/forms/Form'
import { filterFields } from './filters'
import useRemoteSelectOptions from '../../../hooks/useRemoteSelectOptions'
import Button from '../../../components/Button'
import { Alert, Box } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useOrgan } from '../../../hooks/useOrgan'
import useSelectOptions from '../../../hooks/useSelectOptions'
import { useReport } from '../../../context/ReportContext'

const CriticalStockReport = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { selectedOrgan } = useOrgan()
  const { setExportId, isExporting } = useReport()

  const [filtersSection] = filterFields
  const { formData, handleChange } = useForm(
    setDefaultFieldValues([filtersSection]),
    [filtersSection]
  )

  const { get: baseServiceGet } = useBaseService(navigate)
  const { hideLoader, showLoader } = useLoader()
  const { showNotification } = useNotification()

  const stockStatusOptions = useMemo(
    () => [
      { value: 'below', label: 'Abaixo do Mínimo (inclui zerados)' },
      { value: 'zero', label: 'Saldo Zerado' },
    ],
    []
  )

  const stockStatusSelect = useSelectOptions({
    fieldId: 'filter.stock_status',
    formData,
    setFormData: undefined,
    rawOptions: stockStatusOptions,
    mapToOption: (item) => ({ value: item.value, label: item.label }),
  })

  const typesOptions = useRemoteSelectOptions({
    queryKey: ['types'],
    entityPath: entities.types.get,
    baseServiceGet,
    fieldId: 'filter.type_id',
    formData,
    isMulti: true,
    perPage: 15,
    mapToOption: (item) => ({ value: item.id, label: item.name }),
  })

  const groupsOptions = useRemoteSelectOptions({
    queryKey: ['groups'],
    entityPath: entities.groups.get,
    baseServiceGet,
    extraFilters: {
      'types.id': { $in: formData.filter.type_id?.map((id) => id.value) ?? [] },
    },
    fieldId: 'filter.group_id',
    formData,
    isMulti: true,
    perPage: 15,
    mapToOption: (item) => ({ value: item.id, label: item.name }),
  })

  const brandsOptions = useRemoteSelectOptions({
    queryKey: ['brands'],
    entityPath: entities.brands.get,
    baseServiceGet,
    fieldId: 'filter.brand_id',
    formData,
    isMulti: true,
    perPage: 15,
    mapToOption: (item) => ({ value: item.id, label: item.name }),
  })

  const selects = {
    'filter.stock_status': stockStatusSelect,
    'filter.type_id': typesOptions,
    'filter.group_id': groupsOptions,
    'filter.brand_id': brandsOptions,
  }

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      if (!selectConfig) return {}
      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      }
    },
    [selects]
  )

  const getOptions = useCallback(
    (fieldId) => selects[fieldId]?.options || [],
    [selects]
  )
  const getSelectedValue = useCallback(
    (fieldId) => selects[fieldId]?.selectedValue || null,
    [selects]
  )

  const exportReport = useMutation({
    mutationFn: async (type) => {
      showLoader()
      const formated = cleanPayload(formData.filter)
      const params = {
        filters: {
          ...formated,
          type_id: formated.type_id?.map((t) => t.value),
          group_id: formated.group_id?.map((g) => g.value),
          brand_id: formated.brand_id?.map((b) => b.value),
          organization_id: selectedOrgan?.id,
        },
      }
      const response = await baseServiceGet(
        entities.reports.criticalStock.get,
        params,
        { type }
      )
      return response
    },
    onSuccess: (response) => {
      if (response?.status === 202 || response?.status === 200) {
        const generated_id = response.result?.export_id
        if (generated_id) {
          setExportId(generated_id)
          localStorage.setItem('report_exportId', generated_id.toString())
          showNotification(
            'info',
            'Processamento do relatório iniciado em segundo plano.'
          )
        }
      }
    },
    onError: (error) => {
      console.error('onError ', error)
      hideLoader()
    },
    onSettled: () => hideLoader(),
  })

  useEffect(() => {
    if (location.state?.message) {
      showNotification('error', location.state.message)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, showNotification, navigate, location.pathname])

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader title="Estoque Crítico" showBackButton={false} />
      <Alert severity="warning" sx={{ mb: 2 }}>
        <strong>Atenção:</strong> Este relatório apresenta insumos com saldo
        zerado ou em nível igual/abaixo do estoque mínimo cadastrado.
      </Alert>
      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          onSubmit={() => {}}
          textSubmit="Filtrar"
          textLoadingSubmit="Filtrando..."
          showButtons={false}
          disabledSubmitButton={true}>
          {() => (
            <>
              <div key={filtersSection.section}>
                <SectionHeader section={filtersSection.section} />
                <SimpleBody
                  fields={filtersSection.fields}
                  formErrors={{}}
                  formData={formData}
                  handleFieldChange={handleChange}
                  getOptions={getOptions}
                  getSelectedValue={getSelectedValue}
                  getSelectMeta={getSelectMeta}
                />
              </div>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', my: 4, width: '100%' }}>
                <Button
                  variant="outlined"
                  color="primary"
                  type="button"
                  startIcon={<FontAwesomeIcon icon={faFilePdf} />}
                  text="Exportar em PDF"
                  disabled={isExporting}
                  onClick={async () => {
                    try {
                      await exportReport.mutateAsync('pdf')
                    } catch (error) {
                      console.error('Erro ao exportar PDF:', error)
                    }
                  }}
                />
              </Box>
            </>
          )}
        </Form>
      </div>
    </MainLayout>
  )
}

export default CriticalStockReport